import React from 'react'
// using i18n directly from utils; no local translation hook needed
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibHealthCareIcon } from '@chewy/kib-icons-react'
import { type Nil, PermissionArea } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import {
  getCRUDByArea,
  getCurrentBusinessHasActiveWellnessPlans,
  getCurrentBusinessWellnessPlansEnabled,
} from '~/store/reducers/auth'
import { getPatient } from '~/store/reducers/patients'
import { getPatientStatus } from '~/utils'
import useDialog from '~/utils/useDialog'

import {
  getPatientHasMembership,
  getPatientHasNoMembership,
  getPatientHasPausedPlan,
  getPlanDisplayLabel,
} from './wellnessPlanUtils'

import styles from './RhapWellnessPlanMemberButton.module.scss'

export interface RhapWellnessPlanMemberButtonProps {
  className?: string
  clientId: string | Nil
  isLoading?: boolean
  patientId: string | Nil
  readonly?: boolean
}

const RhapWellnessPlanMemberButton = ({
  clientId,
  isLoading,
  patientId,
  readonly: readonlyProp = false,
  className,
}: RhapWellnessPlanMemberButtonProps) => {
  const patient = useSelector(getPatient(patientId))

  const {
    create: wellnessPlanCreatePermissions,
    read: wellnessPlanReadPermissions,
  } = useSelector(getCRUDByArea(PermissionArea.WELLNESS_PLAN))

  const wellnessPlansEnabled = useSelector(
    getCurrentBusinessWellnessPlansEnabled,
  )
  const hasActiveWellnessPlans = useSelector(
    getCurrentBusinessHasActiveWellnessPlans,
  )

  const [openMembershipSignUpDialog] = useDialog(DialogNames.MEMBERSHIP_SIGN_UP)
  const [openPatientMembershipDialog] = useDialog(
    DialogNames.PATIENT_MEMBERSHIP,
  )

  const hasNoMembership = getPatientHasNoMembership(patient)
  const shouldHideSignUp =
    hasNoMembership &&
    (getPatientStatus(patient).disabled || !hasActiveWellnessPlans)
  const readonly =
    readonlyProp ||
    (hasNoMembership
      ? !wellnessPlanCreatePermissions
      : !wellnessPlanReadPermissions)

  const handleClick = () => {
    if (hasNoMembership) {
      openMembershipSignUpDialog({ clientId, patientId })
    } else {
      openPatientMembershipDialog({ clientId, patientId })
    }
  }

  if (isLoading) {
    return <div className={styles.skeleton} data-testid="skeleton" />
  }

  if (!patientId || !wellnessPlansEnabled || shouldHideSignUp) {
    return null
  }

  const planNameLabel = getPlanDisplayLabel(patient)
  const isActive = getPatientHasMembership(patient)
  const isPaused = getPatientHasPausedPlan(patient)

  const buttonStyle: React.CSSProperties | undefined = isPaused
    ? { backgroundColor: 'var(--rhap-color-attention)' }
    : isActive
      ? { backgroundColor: 'var(--rhap-color-success)' }
      : undefined

  return (
    <KibButtonNew
      className={className}
      disabled={readonly}
      emphasis="primary"
      icon={<KibHealthCareIcon />}
      iconPosition="start"
      size="small"
      style={buttonStyle}
      theme={hasNoMembership ? 'utility' : 'action'}
      onClick={readonly ? undefined : handleClick}
    >
      {planNameLabel}
    </KibButtonNew>
  )
}

export default RhapWellnessPlanMemberButton
